<template>
  <button
    :type="type"
    :disabled="disabled || loading"
    :class="buttonClasses"
    @click="handleClick"
  >
    <span v-if="loading" class="loading-spinner"></span>
    <slot v-else />
  </button>
</template>

<script setup lang="ts">
import { computed } from 'vue';

interface Props {
  variant?: 'primary' | 'secondary' | 'ghost';
  size?: 'sm' | 'md' | 'lg';
  disabled?: boolean;
  loading?: boolean;
  type?: 'button' | 'submit' | 'reset';
  fullWidth?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  variant: 'primary',
  size: 'md',
  disabled: false,
  loading: false,
  type: 'button',
  fullWidth: false
});

const emit = defineEmits<{
  click: [event: MouseEvent];
}>();

const buttonClasses = computed(() => {
  const baseClasses = 'btn';
  const variantClass = `btn-${props.variant}`;
  const sizeClass = `btn-${props.size}`;
  const widthClass = props.fullWidth ? 'w-full' : '';
  const disabledClass = props.disabled || props.loading ? 'opacity-50 cursor-not-allowed' : '';

  return [baseClasses, variantClass, sizeClass, widthClass, disabledClass].join(' ');
});

function handleClick(event: MouseEvent) {
  if (!props.disabled && !props.loading) {
    emit('click', event);
  }
}
</script>

<style scoped lang="scss">
.btn {
  @apply inline-flex items-center justify-center font-semibold rounded-full transition-all duration-300;
  @apply focus:outline-none focus:ring-2 focus:ring-offset-2;
  
  &:active {
    transform: scale(0.97);
  }
}

.btn-primary {
  @apply bg-green-500 text-white;
  @apply hover:bg-green-600;
  @apply focus:ring-green-500;
  background: $spotify-green;
  
  &:hover:not(:disabled) {
    background: darken($spotify-green, 8%);
  }
}

.btn-secondary {
  @apply bg-gray-700 text-white;
  @apply hover:bg-gray-600;
  @apply focus:ring-gray-500;
}

.btn-ghost {
  @apply bg-transparent text-white border-2 border-white;
  @apply hover:bg-white hover:text-black;
  @apply focus:ring-white;
}

.btn-sm {
  @apply px-4 py-2 text-sm;
}

.btn-md {
  @apply px-6 py-3 text-base;
}

.btn-lg {
  @apply px-8 py-4 text-lg;
}

.loading-spinner {
  @apply w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin;
}
</style>